import React, { useState, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CropFreeIcon from '@mui/icons-material/CropFree';
import MapIcon from '@mui/icons-material/Map';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridOffIcon from '@mui/icons-material/GridOff';
// Icono 3D global — reactivar junto con toggle getbuildingsbybounds en MapPage + useBuildingsLayer
// import ViewInArIcon from '@mui/icons-material/ViewInAr';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LayersIcon from '@mui/icons-material/Layers';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ContrastIcon from '@mui/icons-material/Contrast';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import ReferenceLayersPanel from '../ReferenceLayersPanel/ReferenceLayersPanel';
import ReferencePlacesPanel from '../ReferencePlacesPanel/ReferencePlacesPanel';
import MapStatusBanner from '../MapStatusBanner/MapStatusBanner';
import MapCoordsReadout from './MapCoordsReadout';

/**
 * Controles flotantes del mapa:
 *  - Toggle satélite / calle
 *  - Toggle visibilidad de parcelas
 *  - Fondo oscuro / claro del plano CAD (`canvas` / `image`)
 *  - Capas de referencia (inundación / pobreza / zonificación; mapa logueado)
 *  - Lugares de referencia en shares de mapa (escuelas, etc.)
 *  - Input de filtro por estado (visible solo si showParcelas=true)
 */

import {
  controlButtonStyle,
  controlButtonHoverBg,
  verticalGroupStyle,
  verticalActionButtonStyle,
  // verticalActionButtonDisabledStyle,
  verticalSeparatorStyle,
  zoomPanelStyle,
  zoomButtonStyle,
  navControlsContainerStyle,
  overlayLegendContainerStyle,
  mapControlsNavBottom,
  feedbackHintAnchorStyle,
  feedbackHintCaretStyle,
  feedbackHintBannerStyle,
  // disabledFilterStyle,
} from './MapControls.styles';
import { layout } from '../../../styles/theme';

import LayerSelectorPanel from '../LayerSelectorPanel/LayerSelectorPanel';

const noop = () => {};

const FEEDBACK_HINT_SESSION_KEY = 'terrio_show_feedback_hint';
const FEEDBACK_HINT_DURATION_MS = 7000;
const FEEDBACK_TOOLTIP_TITLE = 'Enviar feedback';
const FEEDBACK_HINT_TITLE = 'Tu feedback nos ayuda a mejorar Terrio';
const FEEDBACK_BTN_SIZE = layout.sidebarItemHeight;
const FEEDBACK_ICON_SIZE = layout.sidebarIconSize;
const FEEDBACK_BTN_LEFT = (layout.sidebarRailWidth - FEEDBACK_BTN_SIZE) / 2;

const CompassNeedle = ({ bearing }) => (
  <svg
    viewBox="0 0 24 24"
    width="28"
    height="28"
    style={{
      transform: `rotate(${-bearing}deg)`,
      transition: 'transform 0.15s ease-out',
      display: 'block',
    }}
    aria-hidden
  >
    <polygon points="12,3 13.5,12 12,10.5 10.5,12" fill="#ffffff" />
    <polygon points="12,21 13.5,12 12,13.5 10.5,12" fill="rgba(255,255,255,0.38)" />
  </svg>
);

// ─── MapControls ──────────────────────────────────────────────────────────────

const MapControls = ({
  mapView,
  onSelectMapView,   // (viewId: 'satellite1'|'satellite2'|'street') => void
  showParcelas,
  onToggleParcelas,
  parcelasStateFilter,
  onParcelasFilterChange,
  isLoggedIn,
  onCenterOnUserLocation,
  autoPositionEnabled,
  isGeolocating = false,
  /**
   * `geolocate` (default): GPS, resto de Terrio.
   * `fit`: encuadra el overlay / lista actual (showroom CAD, lista compartida).
   */
  centerButtonMode = 'geolocate',
  /** Tooltip del botón central; default según `centerButtonMode`. */
  centerButtonTooltip,
  onZoomIn,
  onZoomOut,
  mapInfo,
  /** Mapa a seguir en vivo para la lectura de coordenadas (`useMapViewInfo().viewMap`). */
  liveMap = null,
  disabledMessage,
  canSendFeedback = false,
  hideFeedbackButton = false,
  /** Oculta el toggle de parcelas catastrales (p. ej. showroom CAD). */
  hideParcelasToggle = false,
  /** Oculta el selector satélite / calle (p. ej. plano CAD sin teselas). */
  hideMapViewToggle = false,
  /**
   * Plano CAD: fondo `dark` | `light`.
   * Si hay `onPlanBackgroundChange`, muestra el botón en el hueco del mapa.
   */
  planBackground = 'dark',
  onPlanBackgroundChange,
  /** Oculta el panel de capas de referencia. */
  hideReferenceLayersToggle = false,
  /**
   * Switcher de lugares (escuelas, etc.) en vistas share de mapa.
   * Independiente de `hideReferenceLayersToggle`.
   */
  showReferencePlacesToggle = false,
  referencePlacesLoading = false,
  referencePlacesLocked = false,
  referencePlaceKinds = [],
  enabledReferencePlaceKinds = [],
  onToggleReferencePlaceKind = noop,
  referencePlacesViewportHint = '',
  feedbackOpen: feedbackOpenProp,
  onFeedbackOpenChange,
  showPobrezaCronicaArgentina = false,
  pobrezaCronicaArgentinaOpacity = 0.34,
  onTogglePobrezaCronicaArgentina = noop,
  onPobrezaCronicaArgentinaOpacityChange = noop,
  showZonasInundables = false,
  onToggleZonasInundables = noop,
  showZonificacionUrbana = false,
  onToggleZonificacionUrbana = noop,
  mapBearing = 0,
  mapPitch = 0,
  onResetNorth = noop,
  onResetPitch = noop,
  deferFeedbackHint = false,
  feedbackHintTrigger = 0,
  /** Reserva inferior extra (ej. footer branded en vistas compartidas). */
  bottomOffset = 0,
  /** Reserva superior extra (ej. barra de búsqueda mobile bajo el header). */
  topOffset = 0,
  /** Leyenda cartográfica a la izquierda del stack de capas (p. ej. stock CAD). */
  overlayLegend = null,
  /**
   * Chrome compacto de vistas compartidas (`lg` hacia abajo).
   * No usar en MapPage: si hay selector de vista, lo mueve al nav
   * inferior en el lugar del 2D. La leyenda de stock CAD sigue visible.
   */
  compactShareChrome = false,
}) => {
  const safeBottomOffset = Math.max(0, Number(bottomOffset) || 0);
  const safeTopOffset = Math.max(0, Number(topOffset) || 0);
  const [layerPanelOpen, setLayerPanelOpen] = useState(false);
  const [referenceLayersPanelOpen, setReferenceLayersPanelOpen] = useState(false);
  const [referencePlacesPanelOpen, setReferencePlacesPanelOpen] = useState(false);
  const [hoveredControl, setHoveredControl] = useState(null);
  const [feedbackOpenInternal, setFeedbackOpenInternal] = useState(false);
  const [feedbackHintVisible, setFeedbackHintVisible] = useState(false);
  const isFeedbackControlled = feedbackOpenProp !== undefined;
  const feedbackOpen = isFeedbackControlled ? feedbackOpenProp : feedbackOpenInternal;
  const setFeedbackOpen = (next) => {
    if (onFeedbackOpenChange) onFeedbackOpenChange(next);
    else setFeedbackOpenInternal(next);
  };

  useEffect(() => {
    if (!canSendFeedback || deferFeedbackHint) return undefined;

    let shouldShowHint = false;
    try {
      shouldShowHint = sessionStorage.getItem(FEEDBACK_HINT_SESSION_KEY) === '1';
      if (shouldShowHint) sessionStorage.removeItem(FEEDBACK_HINT_SESSION_KEY);
    } catch (_) {
      return undefined;
    }

    if (!shouldShowHint) return undefined;

    setFeedbackHintVisible(true);
  }, [canSendFeedback, deferFeedbackHint, feedbackHintTrigger]);

  useEffect(() => {
    if (hideFeedbackButton) setFeedbackHintVisible(false);
  }, [hideFeedbackButton]);

  useEffect(() => {
    if (!showReferencePlacesToggle) setReferencePlacesPanelOpen(false);
  }, [showReferencePlacesToggle]);

  // Cerrar el aviso de feedback después de 7 segundos
  useEffect(() => {
    if (!feedbackHintVisible) return undefined;
    const timer = window.setTimeout(() => setFeedbackHintVisible(false), FEEDBACK_HINT_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [feedbackHintVisible]);

  const isFitCenterMode = centerButtonMode === 'fit';
  const locationTooltipTitle = centerButtonTooltip
    || (isFitCenterMode
      ? 'Encuadrar'
      : (autoPositionEnabled
        ? 'Desactivar posicionamiento automático'
        : 'Activar posicionamiento automático'));

  const interactiveButtonStyle = ({
    controlId,
    active = false,
    disabled = false,
    dimmed = false,
    baseStyle = verticalActionButtonStyle,
  }) => {
    const isHovered = hoveredControl === controlId && !disabled;
    const isPanelTrigger = controlId !== 'parcelas'; // parcelas no tiene estado activo persistente
    const isActive = Boolean(active && isPanelTrigger);

    const idleColor = dimmed ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.88)';
    const color = disabled
      ? 'rgba(255,255,255,0.35)'
      : isActive || isHovered
        ? '#ffffff'
        : idleColor;

    return {
      ...baseStyle,
      backgroundColor: isHovered && !disabled
        ? 'rgba(255,255,255,0.08)'
        : isActive
          ? 'rgba(255,255,255,0.06)'
          : 'transparent',
      color,
      opacity: disabled ? 0.45 : 1,
      transform: 'none',
      transition: 'color 0.18s ease, background-color 0.18s ease',
      cursor: disabled ? 'not-allowed' : 'pointer',
    };
  };

  const navButtonStyle = (controlId, { disabled = false } = {}) => {
    const isHovered = hoveredControl === controlId && !disabled;

    return {
      ...controlButtonStyle,
      opacity: disabled ? 0.6 : 1,
      backgroundColor: isHovered && !disabled
        ? controlButtonHoverBg
        : controlButtonStyle.backgroundColor,
      transition: 'background-color 0.18s ease, opacity 0.18s ease',
      cursor: disabled ? 'not-allowed' : 'pointer',
    };
  };

  const zoomControlButtonStyle = (controlId) => {
    const isHovered = hoveredControl === controlId;

    return {
      ...zoomButtonStyle,
      backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
      color: '#ffffff',
      transition: 'background-color 0.18s ease',
    };
  };

  const handleSelectView = (viewId) => {
    onSelectMapView(viewId);
    setLayerPanelOpen(false);
  };

  const isTilted = mapPitch > 5;
  const isNorthUp = Math.abs(mapBearing) < 0.5;
  const showPlanBackgroundToggle = typeof onPlanBackgroundChange === 'function';
  const showMapViewInTopStack = !hideMapViewToggle && !compactShareChrome;
  const showMapViewInNavStack = !hideMapViewToggle && compactShareChrome;
  const showPitchReset = isTilted && !showMapViewInNavStack;
  const showOverlayLegend = Boolean(overlayLegend);
  const showStyleToggles = !hideParcelasToggle
    || showMapViewInTopStack
    || !hideReferenceLayersToggle
    || showReferencePlacesToggle
    || showPlanBackgroundToggle;

  return (
    <>
      <style>{`
        @keyframes mapControlsFadeIn {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes feedbackHintIn {
          from { opacity: 0; transform: translateY(-50%) translateX(-8px); }
          to { opacity: 1; transform: translateY(-50%) translateX(0); }
        }
      `}</style>

      {/* Panel selector de capas */}
      {layerPanelOpen && !hideMapViewToggle && (
        <LayerSelectorPanel
          currentView={mapView}
          onSelect={handleSelectView}
          onClose={() => setLayerPanelOpen(false)}
          isLoggedIn={isLoggedIn}
          disabledMessage={disabledMessage}
          topOffset={safeTopOffset}
          bottomOffset={safeBottomOffset}
          anchor={showMapViewInNavStack ? 'bottom' : 'top'}
        />
      )}

      {referenceLayersPanelOpen && !hideReferenceLayersToggle && (
        <ReferenceLayersPanel
          onClose={() => setReferenceLayersPanelOpen(false)}
          showPobrezaCronicaArgentina={showPobrezaCronicaArgentina}
          onTogglePobrezaCronicaArgentina={onTogglePobrezaCronicaArgentina}
          showZonasInundables={showZonasInundables}
          onToggleZonasInundables={onToggleZonasInundables}
          showZonificacionUrbana={showZonificacionUrbana}
          onToggleZonificacionUrbana={onToggleZonificacionUrbana}
          isLoggedIn={isLoggedIn}
          mapZoom={mapInfo?.zoom}
          topOffset={safeTopOffset}
        />
      )}

      {referencePlacesPanelOpen && showReferencePlacesToggle && (
        <ReferencePlacesPanel
          kinds={referencePlaceKinds}
          enabledKinds={enabledReferencePlaceKinds}
          onToggleKind={onToggleReferencePlaceKind}
          locked={referencePlacesLocked}
          loading={referencePlacesLoading}
          viewportHint={referencePlacesViewportHint}
          onClose={() => setReferencePlacesPanelOpen(false)}
          topOffset={safeTopOffset}
        />
      )}

      {/* Grupo vertical de controles */}
      {showStyleToggles ? (
      <div style={verticalGroupStyle({ topOffset: safeTopOffset })}>

        {!hideParcelasToggle && (
          <Tooltip title={showParcelas ? 'Ocultar lotes' : 'Mostrar lotes'} placement="left">
            <button
              type="button"
              data-tour="toggle-parcelas"
              onClick={onToggleParcelas}
              style={interactiveButtonStyle({
                controlId: 'parcelas',
                active: false,
                dimmed: !showParcelas,
              })}
              onMouseEnter={() => setHoveredControl('parcelas')}
              onMouseLeave={() => setHoveredControl(null)}
            >
              {showParcelas ? <GridOnIcon fontSize="small"/> : <GridOffIcon fontSize="small"/>}
            </button>
          </Tooltip>
        )}

        {showMapViewInTopStack && (
        <Tooltip title="Cambiar vista del mapa" placement="left">
          <button
            type="button"
            data-tour="map-style"
            onClick={() => {
              setReferencePlacesPanelOpen(false);
              setLayerPanelOpen((prev) => !prev);
            }}
            style={interactiveButtonStyle({
              controlId: 'layers',
              active: layerPanelOpen,
            })}
            onMouseEnter={() => setHoveredControl('layers')}
            onMouseLeave={() => setHoveredControl(null)}
          >
            <MapIcon fontSize="small" />
          </button>
        </Tooltip>
        )}

        {showPlanBackgroundToggle && (
        <Tooltip
          title={planBackground === 'dark' ? 'Fondo claro' : 'Fondo oscuro'}
          placement="left"
        >
          <button
            type="button"
            aria-label={planBackground === 'dark' ? 'Usar fondo claro' : 'Usar fondo oscuro'}
            onClick={() => onPlanBackgroundChange(planBackground === 'dark' ? 'light' : 'dark')}
            style={interactiveButtonStyle({
              controlId: 'plan-background',
              active: planBackground === 'light',
            })}
            onMouseEnter={() => setHoveredControl('plan-background')}
            onMouseLeave={() => setHoveredControl(null)}
          >
            <ContrastIcon fontSize="small" />
          </button>
        </Tooltip>
        )}

        {!hideReferenceLayersToggle && (
        <Tooltip title="Capas de referencia" placement="left">
          <button
            type="button"
            data-tour="capas"
            onClick={() => {
              setReferencePlacesPanelOpen(false);
              setReferenceLayersPanelOpen((prev) => !prev);
            }}
            style={{
              ...interactiveButtonStyle({
                controlId: 'reference-layers',
                active: referenceLayersPanelOpen,
              }),
              position: 'relative',
            }}
            onMouseEnter={() => setHoveredControl('reference-layers')}
            onMouseLeave={() => setHoveredControl(null)}
          >
            <LayersIcon fontSize="small" />
            {isLoggedIn && (
              showPobrezaCronicaArgentina
              || showZonasInundables
              || showZonificacionUrbana
            ) && (
              <span style={{
                position: 'absolute',
                top: '3px',
                right: '3px',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#489291',
              }} />
            )}
          </button>
        </Tooltip>
        )}

        {showReferencePlacesToggle && (
        <Tooltip title="Puntos de interés" placement="left">
          <button
            type="button"
            data-tour="puntos-interes"
            aria-label="Puntos de interés"
            onClick={() => {
              setReferenceLayersPanelOpen(false);
              setLayerPanelOpen(false);
              setReferencePlacesPanelOpen((prev) => !prev);
            }}
            style={{
              ...interactiveButtonStyle({
                controlId: 'reference-places',
                active: referencePlacesPanelOpen || enabledReferencePlaceKinds.length > 0,
              }),
              position: 'relative',
            }}
            onMouseEnter={() => setHoveredControl('reference-places')}
            onMouseLeave={() => setHoveredControl(null)}
          >
            <PlaceOutlinedIcon fontSize="small" />
            {enabledReferencePlaceKinds.length > 0 && !referencePlacesLocked && (
              <span style={{
                position: 'absolute',
                top: '3px',
                right: '3px',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#489291',
              }} />
            )}
          </button>
        </Tooltip>
        )}

      </div>
      ) : null}

      {canSendFeedback && !hideFeedbackButton && (
        <>
          <div
            style={{
              position: 'absolute',
              left: `${FEEDBACK_BTN_LEFT}px`,
              bottom: `${12 + safeBottomOffset}px`,
              zIndex: 1300,
              width: `${FEEDBACK_BTN_SIZE}px`,
              height: `${FEEDBACK_BTN_SIZE}px`,
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
              <Tooltip title={FEEDBACK_TOOLTIP_TITLE} placement="right">
                <button
                  type="button"
                  onClick={() => {
                    setFeedbackHintVisible(false);
                    setFeedbackOpen(true);
                  }}
                  aria-label="Abrir formulario de feedback"
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '12px',
                    backgroundColor: feedbackHintVisible ? 'rgba(72,146,145,0.22)' : 'transparent',
                    color: feedbackHintVisible ? '#ffffff' : 'rgba(255,255,255,0.9)',
                    cursor: 'pointer',
                    transition: 'background-color 0.18s ease, transform 0.18s ease, color 0.18s ease, box-shadow 0.18s ease',
                    boxShadow: feedbackHintVisible ? '0 0 0 1px rgba(72,146,145,0.35)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (feedbackHintVisible) return;
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    if (feedbackHintVisible) return;
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HelpOutlineIcon style={{ fontSize: FEEDBACK_ICON_SIZE, width: FEEDBACK_ICON_SIZE, height: FEEDBACK_ICON_SIZE }} />
                </button>
              </Tooltip>
            </div>

            {feedbackHintVisible && (
              <div style={feedbackHintAnchorStyle}>
                <span aria-hidden style={feedbackHintCaretStyle} />
                <MapStatusBanner
                  placement="inline"
                  onClose={() => setFeedbackHintVisible(false)}
                  closeAriaLabel="Cerrar aviso de feedback"
                  style={feedbackHintBannerStyle}
                >
                  {FEEDBACK_HINT_TITLE}
                </MapStatusBanner>
              </div>
            )}
          </div>
          <FeedbackModal
            isOpen={feedbackOpen}
            onClose={() => setFeedbackOpen(false)}
          />
        </>
      )}

      {showOverlayLegend ? (
        <div style={overlayLegendContainerStyle({ topOffset: safeTopOffset })}>
          {overlayLegend}
        </div>
      ) : null}

      <div
        data-tour="nav-controles"
        style={{
          ...navControlsContainerStyle,
          bottom: mapControlsNavBottom(safeBottomOffset),
        }}
      >
        {showMapViewInNavStack && (
          <Tooltip title="Cambiar vista del mapa" placement="left">
            <button
              type="button"
              data-tour="map-style"
              onClick={() => {
                setReferencePlacesPanelOpen(false);
                setLayerPanelOpen((prev) => !prev);
              }}
              aria-label="Cambiar vista del mapa"
              style={{
                ...navButtonStyle('layers'),
                animation: 'mapControlsFadeIn 0.2s ease',
              }}
              onMouseEnter={() => setHoveredControl('layers')}
              onMouseLeave={() => setHoveredControl(null)}
            >
              <MapIcon fontSize="small" />
            </button>
          </Tooltip>
        )}

        {showPitchReset && (
          <Tooltip
            title={`Vista cenital (inclinación actual: ${Math.round(mapPitch)}°)`}
            placement="left"
          >
            <button
              type="button"
              onClick={onResetPitch}
              aria-label="Volver a vista cenital"
              style={{
                ...navButtonStyle('reset-pitch'),
                animation: 'mapControlsFadeIn 0.2s ease',
              }}
              onMouseEnter={() => setHoveredControl('reset-pitch')}
              onMouseLeave={() => setHoveredControl(null)}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#ffffff',
                }}
              >2D</span>
            </button>
          </Tooltip>
        )}

        <Tooltip
          title={locationTooltipTitle}
          placement="left"
        >
          <button
            type="button"
            onClick={onCenterOnUserLocation}
            disabled={!isFitCenterMode && isGeolocating}
            aria-label={locationTooltipTitle}
            style={navButtonStyle('location', { disabled: !isFitCenterMode && isGeolocating })}
            onMouseEnter={() => setHoveredControl('location')}
            onMouseLeave={() => setHoveredControl(null)}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}
            >
              {isFitCenterMode ? <CropFreeIcon fontSize="small" /> : <MyLocationIcon fontSize="small" />}
            </span>
          </button>
        </Tooltip>

        <Tooltip
          title={isNorthUp ? 'Norte' : 'Restablecer al norte'}
          placement="left"
        >
          <button
            type="button"
            onClick={onResetNorth}
            aria-label="Restablecer al norte"
            style={navButtonStyle('compass')}
            onMouseEnter={() => setHoveredControl('compass')}
            onMouseLeave={() => setHoveredControl(null)}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CompassNeedle bearing={mapBearing} />
            </span>
          </button>
        </Tooltip>

        <div style={zoomPanelStyle}>
          <Tooltip title="Acercar" placement="left">
            <button
              onClick={onZoomIn}
              style={zoomControlButtonStyle('zoom-in')}
              onMouseEnter={() => setHoveredControl('zoom-in')}
              onMouseLeave={() => setHoveredControl(null)}
            >
              <AddIcon fontSize="small" />
            </button>
          </Tooltip>
          <div style={verticalSeparatorStyle} />
          <Tooltip title="Alejar" placement="left">
            <button
              onClick={onZoomOut}
              style={zoomControlButtonStyle('zoom-out')}
              onMouseEnter={() => setHoveredControl('zoom-out')}
              onMouseLeave={() => setHoveredControl(null)}
            >
              <RemoveIcon fontSize="small" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Panel de información: lat, lng | zoom */}
      <div
        style={{
          position: 'absolute',
          right: '18px',
          bottom: `${10 + safeBottomOffset}px`,
          zIndex: 1000,
          backgroundColor: 'rgb(28, 28, 28)',
          opacity: 0.8,
          color: '#e5e7eb',
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '11px',
          fontFamily:
            'SF Mono, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          whiteSpace: 'nowrap',
        }}
      >
        <MapCoordsReadout mapInfo={mapInfo} liveMap={liveMap} />
      </div>
    </>
  );
};

export default MapControls;